/**
 * VisaoExecutivaPage.tsx: Visão Executiva consolidada do grupo (rota `/app`).
 *
 * Estado honesto: sem dados, aguardando ingestão de fontes (ADR-001, UX.md §5.2).
 * NÃO exibe dashboard com dados fictícios. Exibe empty state rico e instrutivo.
 *
 * Quando o módulo de ingestão estiver operacional (FASE E), este componente
 * será substituído pelo dashboard real com KPIs, séries temporais e breakdown
 * por unidade (NAVEGACAO.md §7.1, delegado ao agente financial-ux).
 *
 * Repaginação (B10): PageHeader, Card e EmptyState da base @/components/ui;
 * zero hex, radius só via token.
 */

import { useOrbUser } from "@/features/auth/AuthContext";
import { useTenant } from "@/features/shell/TenantContext";
import { PageHeader, Card, EmptyState } from "@/components/ui";

// ---------------------------------------------------------------------------
// Ícone de órbita/dashboard vazio
// ---------------------------------------------------------------------------
function EmptyDashboardIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      style={{ margin: "0 auto", display: "block" }}
    >
      <circle cx="24" cy="24" r="20" stroke="var(--color-bg-border)" strokeWidth="2" />
      <circle cx="24" cy="24" r="6" fill="var(--color-bg-raised)" />
      <ellipse
        cx="24"
        cy="24"
        rx="16"
        ry="6"
        stroke="var(--color-bg-border)"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="4 3"
        transform="rotate(-30 24 24)"
      />
      <ellipse
        cx="24"
        cy="24"
        rx="16"
        ry="6"
        stroke="var(--color-bg-border)"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="4 3"
        transform="rotate(30 24 24)"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Card de próximo passo (Card da base + numeração no acento)
// ---------------------------------------------------------------------------
function NextStepCard({
  step,
  title,
  description,
}: {
  step: string;
  title: string;
  description: string;
}) {
  return (
    <Card padding="1rem 1.25rem" style={{ display: "flex", gap: "1rem" }}>
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "var(--radius-pill)",
          background: "var(--color-accent-subtle)",
          border: "1px solid var(--color-accent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: "var(--color-accent)",
          flexShrink: 0,
        }}
      >
        {step}
      </div>
      <div>
        <p
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "var(--color-text-primary)",
            margin: "0 0 0.25rem",
          }}
        >
          {title}
        </p>
        <p
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.8125rem",
            color: "var(--color-text-muted)",
            margin: 0,
          }}
        >
          {description}
        </p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
export function VisaoExecutivaPage() {
  const orbUser = useOrbUser();
  const { branding } = useTenant();

  return (
    <div
      style={{
        maxWidth: "760px",
        margin: "0 auto",
        padding: "2rem 1.5rem",
      }}
    >
      {/* Cabeçalho padrão da base (PageHeader) */}
      <PageHeader
        eyebrow="Visão Geral"
        title="Visão Executiva"
        description={`${branding.displayName} · Consolidado`}
      />

      {/* Empty state principal (EmptyState da base) */}
      <div style={{ marginBottom: "1.5rem" }}>
        <EmptyState
          icon={<EmptyDashboardIcon />}
          title="Sem dados, aguardando ingestão"
          description="O dashboard executivo será exibido aqui após a configuração das fontes de dados (SAP S/4HANA, DataSig, planilhas XLSX). Nenhum dado fictício é exibido, só números reais com rastreabilidade de origem."
          action={
            <span
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: "0.625rem",
                color: "var(--color-text-disabled)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Autenticado como {orbUser?.email ?? "usuário"} · papel{" "}
              {orbUser?.claims.role ?? "sem papel"}
            </span>
          }
        />
      </div>

      {/* Próximos passos: orientação ao admin */}
      {orbUser?.claims.role === "admin" && (
        <section aria-label="Próximos passos para configuração">
          <h3
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--color-text-secondary)",
              margin: "0 0 0.75rem",
            }}
          >
            Próximos passos para ativar o Órbita
          </h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.625rem",
            }}
          >
            <NextStepCard
              step="1"
              title="Configurar estrutura do grupo"
              description="Acesse Estrutura para registrar as unidades e centros de custo do tenant."
            />
            <NextStepCard
              step="2"
              title="Conectar fontes de dados"
              description="Acesse Integrações para configurar SAP S/4HANA, DataSig e Spincare."
            />
            <NextStepCard
              step="3"
              title="Criar usuários do tenant"
              description="Acesse Usuários para adicionar membros da equipe e atribuir papéis."
            />
            <NextStepCard
              step="4"
              title="Primeira ingestão de dados"
              description="Após conectar as fontes, a primeira sincronização populará os módulos financeiros."
            />
          </div>
        </section>
      )}

      {/* Orientação para não-admin (Card da base com fundo de acento) */}
      {orbUser?.claims.role !== "admin" && (
        <Card
          padding="1rem 1.25rem"
          style={{
            background: "var(--color-accent-subtle)",
            border: "1px solid var(--color-accent)",
          }}
        >
          <p
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.875rem",
              color: "var(--color-text-primary)",
              margin: 0,
            }}
          >
            O ambiente está sendo configurado pelo administrador do grupo.
            Os dados aparecerão aqui assim que as fontes estiverem conectadas.
          </p>
        </Card>
      )}
    </div>
  );
}
